/**
 * The journey: which day of the hundred it is, the phase it falls in, that
 * phase's principles and milestones, and the doors into the work.
 */

import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { getAppDefinition } from '../registry';
import { AppShell, ScreenHeading } from '../components/AppShell';
import { AppCard, Eyebrow } from '../components/primitives';
import { DAILY_PROMPTS, PHASES } from './framework';
import { conversationsIn, discoveredPriorities, latestDay101Decision } from './first100Days';
import { useJourney } from './useJourney';
import NoMandate from './NoMandate';

export default function JourneyScreen() {
  const app = getAppDefinition('first-100-days')!;
  const navigate = useNavigate();
  const items = useItemsStore((s) => s.items);
  const { loaded, transition, stakeholders, decisions, day, phase } = useJourney();

  if (!loaded) {
    return (
      <AppShell app={app}>
        <p className="font-body text-sm text-[var(--text-ter)]">Reading your mandate…</p>
      </AppShell>
    );
  }
  if (!transition) {
    return (
      <AppShell app={app}>
        <NoMandate />
      </AppShell>
    );
  }

  const conversations = stakeholders.reduce((n, s) => n + conversationsIn(s.notes).length, 0);
  const sorted = discoveredPriorities(items).length;
  const day101 = latestDay101Decision(decisions);
  const prompt = day !== null ? DAILY_PROMPTS[day] : undefined;

  const doors = [
    { to: 'listening', title: 'Listening tour', line: `${conversations} conversation${conversations === 1 ? '' : 's'} logged with ${stakeholders.length} ${stakeholders.length === 1 ? 'person' : 'people'} on your map` },
    { to: 'priorities', title: 'Priority discovery', line: `${sorted} ${sorted === 1 ? 'priority' : 'priorities'} sorted by what you'll do with ${sorted === 1 ? 'it' : 'them'}` },
    { to: 'day-101', title: 'Day 101', line: day101 ? 'Your first priorities are ranked and recorded.' : 'Rank what you will drive, and record your first priorities.' },
    { to: 'meetings', title: 'Meeting playbook', line: 'The first team meeting, first 1:1s, skip-levels, peers and your manager.' },
  ];

  return (
    <AppShell app={app}>
      <ScreenHeading
        title={day !== null ? `Day ${Math.min(day, 100)} of 100` : 'Your first 100 days'}
        subtitle={`${transition.role}${transition.organization ? `, ${transition.organization}` : ''}`}
      />
      {day === null ? (
        <p className="mt-2 font-body text-sm text-[var(--text-sec)]">
          Add your start date to your mandate and the journey will count from it.
        </p>
      ) : null}

      {phase ? (
        <AppCard className="mt-4">
          <Eyebrow>Phase {phase.number} of {PHASES.length} · {phase.subtitle}</Eyebrow>
          <h2 className="mt-1 font-heading font-semibold text-lg text-[var(--text-pri)]">{phase.title}</h2>
          <p className="mt-1 font-body text-sm text-[var(--text-sec)]">{phase.description}</p>
          <ul className="mt-3 list-disc space-y-1 pl-5 font-body text-sm text-[var(--text-sec)]">
            {phase.principles.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
          <div className="mt-4">
            <Eyebrow>Milestones</Eyebrow>
          </div>
          <ul className="mt-2 space-y-2">
            {phase.milestones.map((m) => (
              <li key={m.id} className="font-body text-sm">
                <span className={`font-mono text-xs ${day !== null && day >= m.day ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}`}>
                  Day {m.day}
                </span>{' '}
                <span className="font-medium text-[var(--text-pri)]">{m.title}</span>
                <span className="block text-[var(--text-sec)]">{m.description}</span>
              </li>
            ))}
          </ul>
        </AppCard>
      ) : null}

      {prompt ? (
        <AppCard className="mt-3">
          <Eyebrow>Today's question</Eyebrow>
          <p className="mt-1 font-body text-sm text-[var(--text-pri)]">{prompt}</p>
        </AppCard>
      ) : null}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {doors.map((door) => (
          <AppCard key={door.to} onClick={() => navigate(`/apps/first-100-days/${door.to}`)}>
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-heading font-semibold text-[var(--text-pri)]">{door.title}</p>
                <p className="mt-0.5 font-body text-sm text-[var(--text-sec)]">{door.line}</p>
              </div>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[var(--text-ter)]" />
            </div>
          </AppCard>
        ))}
      </div>
    </AppShell>
  );
}
